// This file is used to configure i18next, a library that allows us to use multiple languages in our app.

import i18n from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";
import {
    registerThirdPartyApiTranslations,
    registerTimezonesTranslations,
    registerWarningsTranslations,
} from "verdi-common/i18n";

import { languages, NAMESPACES, translationPaths } from "./constants";
import { getInitialLocale } from "./locale";

const getFallbackPath = (language: string) => `./locales/${language}/${NAMESPACES.COMMON}.json`;

// Load in all translation files at build time using Vite
// Key: file path (e.g. ./locales/en-CA/features/cards.json)
// Value: the JS Module produced from "import('./locales/...')"
const translationFiles = import.meta.glob("./locales/**/*.json");

i18n.use(
    resourcesToBackend((language: string, namespace: string) => {
        const path = `./locales/${language}/${namespace}.json`;

        // Get the module loader for the relevant translation file from Vite
        let loader = translationFiles[path];

        // If an invalid namespace is requested, use the fallback namespace
        // This will prevent the app from crashing and the translation key will be displayed in app
        if (!loader) {
            console.warn("[i18n] Missing translation file, using fallback", { language, namespace, path });
            loader =
                translationFiles[getFallbackPath(language)] ?? translationFiles[getFallbackPath(languages.default)];
        }
        // return the loaded JS module for the relevant translation file
        return loader();
    }),
)
    .on("failedLoading", (_lng: string, _ns: string, msg: string) => console.error(msg))
    // @ts-ignore this 'use' function exists but is not typed
    .use(initReactI18next) // passes i18n down to react-i18next
    .init({
        lng: getInitialLocale(),
        fallbackLng: languages.default,
        ns: [...Object.values(translationPaths)],
        load: "currentOnly",
        keySeparator: ".",
        interpolation: {
            escapeValue: false, // react already safes from xss
        },
        fallbackNS: NAMESPACES.COMMON,
        // Prevent suspense when loading translation files async (ie. files not included in the ns array on init)
        // This is required since our current React-Leaflet configuration does not support React Suspense
        react: {
            useSuspense: false,
        },
    });

i18n.on("languageChanged", (lng) => {
    document.documentElement.setAttribute("lang", lng);
});

registerWarningsTranslations(i18n);
registerTimezonesTranslations(i18n);
registerThirdPartyApiTranslations(i18n);

export default i18n;
